import { useState, useEffect, useCallback } from 'react';
import { useActiveBroadcastBanners } from '@/hooks/use-broadcast-banners';
import { cn } from '@/lib/utils';
import {
  X,
  Megaphone,
  Info,
  AlertTriangle,
  CheckCircle,
  AlertCircle,
  ArrowRight,
  ExternalLink,
  Bell,
  Calendar,
  Clock,
  FileText,
  Star,
  Heart,
  Zap,
  Globe,
  Shield,
  Lock,
  Mail,
  MessageSquare,
  Gift,
  Rocket,
  Sparkles,
} from 'lucide-react';
import { HtmlContent } from '@/components/ui/html-content';
import { Link } from 'react-router-dom';

const severityDefaults: Record<string, { icon: React.ElementType; bg: string; text: string; badge: string }> = {
  info: {
    icon: Info,
    bg: 'bg-blue-500/15',
    text: 'text-blue-400',
    badge: 'bg-blue-500/20 text-blue-300',
  },
  warning: {
    icon: AlertTriangle,
    bg: 'bg-amber-500/15',
    text: 'text-amber-400',
    badge: 'bg-amber-500/20 text-amber-300',
  },
  success: {
    icon: CheckCircle,
    bg: 'bg-green-500/15',
    text: 'text-green-400',
    badge: 'bg-green-500/20 text-green-300',
  },
  error: {
    icon: AlertCircle,
    bg: 'bg-red-500/15',
    text: 'text-red-400',
    badge: 'bg-red-500/20 text-red-300',
  },
};

// Map iconName strings to Lucide components (module-level, not recreated on render)
const iconMap: Record<string, React.ElementType> = {
  Megaphone, Info, AlertTriangle, CheckCircle, AlertCircle,
  Bell, Calendar, Clock, FileText, Star, Heart, Zap, Globe,
  Shield, Lock, Mail, MessageSquare, Gift, Rocket, Sparkles,
};

export function BroadcastBanner() {
  const { data: banners, isLoading } = useActiveBroadcastBanners();
  const [visibleIds, setVisibleIds] = useState<Set<string>>(new Set());
  const [dismissing, setDismissing] = useState<Set<string>>(new Set());

  // Check localStorage for dismissed banners
  useEffect(() => {
    if (!banners || banners.length === 0) {
      setVisibleIds(new Set());
      return;
    }

    const visible = new Set<string>();
    for (const banner of banners) {
      const key = `broadcast-dismissed-${banner.id}`;
      const stored = localStorage.getItem(key);
      if (stored) {
        try {
          const { version } = JSON.parse(stored);
          if (version >= banner.version) continue; // Still dismissed
        } catch {}
      }
      visible.add(banner.id);
    }
    setVisibleIds(visible);
  }, [banners]);

  const dismiss = useCallback((bannerId: string, version: number) => {
    setDismissing((prev) => new Set(prev).add(bannerId));
    localStorage.setItem(
      `broadcast-dismissed-${bannerId}`,
      JSON.stringify({ version, dismissedAt: new Date().toISOString() })
    );
    // Wait for exit animation, then remove
    setTimeout(() => {
      setVisibleIds((prev) => {
        const next = new Set(prev);
        next.delete(bannerId);
        return next;
      });
      setDismissing((prev) => {
        const next = new Set(prev);
        next.delete(bannerId);
        return next;
      });
    }, 200);
  }, []);

  if (isLoading || !banners || banners.length === 0 || visibleIds.size === 0) {
    return null;
  }

  return (
    <>
      {banners.map((banner) => {
          if (!visibleIds.has(banner.id)) return null;

          const style = severityDefaults[banner.severity] || severityDefaults.info;
          const IconComponent = (banner.iconName && iconMap[banner.iconName]) ? iconMap[banner.iconName] : style.icon;
          const isExternal = banner.ctaLink?.startsWith('http');

          return (
            <div
              key={banner.id}
              className={cn(
                'relative flex flex-col items-center sm:flex-row sm:items-center justify-center gap-2 sm:gap-3 px-4 py-3 transition-all duration-200',
                style.bg,
                dismissing.has(banner.id)
                  ? 'animate-out fade-out slide-out-to-top-2 duration-200 opacity-0 -translate-y-2'
                  : 'animate-in fade-in slide-in-from-top-2 duration-300'
              )}
            >
              {/* Icon */}
              <div className={cn('w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0', style.badge)}>
                <IconComponent size={16} className={style.text} />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <HtmlContent
                  html={banner.message}
                  className="text-sm leading-relaxed [&_a]:text-dyndev-green [&_a]:underline [&_strong]:font-semibold"
                />

                {/* CTA button */}
                {banner.ctaLabel && banner.ctaLink && (
                  <div className="mt-2">
                    {isExternal ? (
                      <a
                        href={banner.ctaLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={cn(
                          'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
                          style.badge
                        )}
                      >
                        {banner.ctaLabel}
                        <ExternalLink size={12} />
                      </a>
                    ) : (
                      <Link
                        to={banner.ctaLink}
                        className={cn(
                          'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
                          style.badge
                        )}
                      >
                        {banner.ctaLabel}
                        <ArrowRight size={12} />
                      </Link>
                    )}
                  </div>
                )}
              </div>              {/* Dismiss button — top-right on mobile, inline on desktop */}
              {banner.isDismissible && (
                <button
                  onClick={() => dismiss(banner.id, banner.version)}
                  className="absolute top-2 right-2 sm:relative sm:top-auto sm:right-auto flex-shrink-0 p-1 rounded-md hover:bg-[var(--bg-secondary)] transition-colors text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                  aria-label="Dismiss banner"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          );
        })}
    </>
  );
}
